
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import {
  ISbStoryData,
  StoryblokComponent,
  getStoryblokApi,
} from '@storyblok/react'
import { NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useEffect } from 'react'
import Layout from '@/app/common/components/Layout'
import { GraphQLOperationError } from '@/app/common/graphql/error'
import { GraphQLPrefetchClient } from '@/app/common/graphql/prefetchClient'
import { getWithRelationsAndLinks } from '@/app/common/storyblok'
import Item from '@/blocks/Item/Item'
import { createGetServerSideProps } from '@/helpers/createGetServerSideProps'
import * as tracking from '@/helpers/tracking'
import { SelectedLegacyProduct } from '@/types'
import {
  GetItemCategoriesDocument,
  GetItemCategoriesQuery,
  GetItemCategoriesQueryVariables,
  GetItemPageContentDocument,
  GetItemPageContentQuery,
  GetItemPageContentQueryVariables,
  GetParentCategoriesDocument,
  GetParentCategoriesQuery,
  GetParentCategoriesQueryVariables,
} from '@/types/gql/graphql'
import { ItemPageStoryblok } from '@/types/storyblok-component-types'
import { isNotFoundError } from '@/utils/error'
import { dayjs } from '@/utils/time'

interface ProductObjectIDProps {
  initialQueryVariables: GetItemPageContentQueryVariables
  selectedProduct: SelectedLegacyProduct
  story?: ISbStoryData<ItemPageStoryblok>
}

const relations = [
  'faq-reference.faqs',
  'reviews-reference.reviews',
  'steps-reference.steps',
]

const ItemPage: NextPage<ProductObjectIDProps> = ({
  initialQueryVariables: getItemPageContentQueryVariables,
  selectedProduct,
  story,
}) => {
  useEffect(() => {
    tracking.viewItemLegacy([selectedProduct])
  }, [selectedProduct])

  return (
    <Layout>
      <NextSeo
        title={
          selectedProduct.title
            ? `${selectedProduct.title}${
                ['GoldJewellery', 'Coins'].includes(
                  selectedProduct.contentCategories?.lvl0 ?? '',
                )
                  ? ' verkaufen oder beleihen'
                  : ''
              }`
            : undefined
        }
        nofollow={true}
        noindex={true}
      />
      {story ? (
        story.content?.body?.map((block) => (
          <StoryblokComponent
            key={block._uid}
            blok={block}
            selectedProduct={selectedProduct}
            initialQueryVariables={getItemPageContentQueryVariables}
          />
        ))
      ) : (
        <Item
          selectedProduct={selectedProduct}
          initialQueryVariables={getItemPageContentQueryVariables}
        />
      )}
    </Layout>
  )
}

 const _getServerSideProps = createGetServerSideProps({
  async getServerSideProps({ context, client }) {
    const now = dayjs().toDate()

    const initialQueryVariables: GetItemPageContentQueryVariables = {
      includeDrafts: !!context.preview,
      slug: context.query.slug as string,
      date: now.toISOString(),
      omitInternalQuestions: true,
    }

    let itemPage: GetItemPageContentQuery | null

    try {
      ;[itemPage] = await Promise.all([
        prefetchItemPageAndParentCategories(client, initialQueryVariables),
        client.executeQuery<
          GetItemCategoriesQuery,
          GetItemCategoriesQueryVariables
        >({
          document: GetItemCategoriesDocument,
        }),
      ])
    } catch (error) {
      if (isNotFoundError(error as GraphQLOperationError))
        return { notFound: true }
      throw error
    }

    if (!itemPage) {
      throw new Error('No item page content found')
    }

    const { getItemPageContent: itemPageContent } = itemPage
    const { product, categoryPages } = itemPageContent
    const selectedProduct: SelectedLegacyProduct = { ...product }
    const { lvl0, lvl1, lvl2, lvl3 } = getContentCategory(selectedProduct)

    if (!lvl0 && !lvl1 && !lvl2 && !lvl3) {
      throw new Error('No content categories found')
    }

    const storyblokApi = getStoryblokApi()

    const { data } = await getWithRelationsAndLinks({
      slug: `cdn/stories`,
      storyblokApi,
      context,
      params: {
        resolve_relations: relations,
        filter_query: {
          category: {
            in: [lvl0, lvl1, lvl2, lvl3].filter(Boolean).join(','),
          },
        },
        content_type: 'item-page',
      },
    })

    const matchingStory = getMatchingStory(data.stories, {
      lvl0,
      lvl1,
      lvl2,
      lvl3,
    })

    if (!matchingStory) {
      return {
        props: {
          initialQueryVariables,
          selectedProduct,
        },
      }
    }

    if (!selectedProduct.picture) {
      const fallbackImage =
        categoryPages.lvl3?.fallbackImage ??
        categoryPages.lvl2?.fallbackImage ??
        categoryPages.lvl1?.fallbackImage ??
        categoryPages.lvl0?.fallbackImage

      selectedProduct.picture = fallbackImage ?? (null as any)
    }

    return {
      props: {
        initialQueryVariables,
        selectedProduct,
        story: matchingStory,
      },
    }
  },
})

const getContentCategory = (selectedProduct: SelectedLegacyProduct) => {
  const { lvl0, lvl1, lvl2, lvl3 } = selectedProduct.contentCategories ?? {}

  const categories = {
    lvl0,
    lvl1: [lvl0, lvl1].filter(Boolean),
    lvl2: [lvl0, lvl1, lvl2].filter(Boolean),
    lvl3: [lvl0, lvl1, lvl2, lvl3].filter(Boolean),
  }

  return {
    lvl0: categories.lvl0 ?? null,
    lvl1: categories.lvl1.length > 0 ? categories.lvl1.join(' / ') : null,
    lvl2: categories.lvl2.length > 0 ? categories.lvl2.join(' / ') : null,
    lvl3: categories.lvl3.length > 0 ? categories.lvl3.join(' / ') : null,
  }
}

const getMatchingStory = (
  stories: Array<ISbStoryData<ItemPageStoryblok>>,
  categories: {
    lvl0: string | null
    lvl1: string | null
    lvl2: string | null
    lvl3: string | null
  },
) => {
  if (!stories || !stories.length) {
    return null
  }

  const storyLvl0 = stories.find((s) => s.content.category === categories.lvl0)
  const storyLvl1 = stories.find((s) => s.content.category === categories.lvl1)
  const storyLvl2 = stories.find((s) => s.content.category === categories.lvl2)
  const storyLvl3 = stories.find((s) => s.content.category === categories.lvl3)

  return storyLvl3 ?? storyLvl2 ?? storyLvl1 ?? storyLvl0
}

async function prefetchItemPageAndParentCategories(
  client: GraphQLPrefetchClient,
  variables: GetItemPageContentQueryVariables,
) {
  const response = await client.executeQuery<
    GetItemPageContentQuery,
    GetItemPageContentQueryVariables
  >({
    document: GetItemPageContentDocument,
    variables,
  })
  const itemPage = response?.data || null
  const product = itemPage?.getItemPageContent.product
  if (product?.itemCategoryId) {
    await client.executeQuery<
      GetParentCategoriesQuery,
      GetParentCategoriesQueryVariables
    >({
      document: GetParentCategoriesDocument,
      variables: {
        itemCategoryId: product.itemCategoryId,
      },
    })
  }
  return itemPage
}

export default ItemPage


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/item/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  